







































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import Dictionary from "@/models/interfaces/Dictionary";
import NumberFormatter from "@/service/NumberFormatter";

export const HEATMAP_SORT_ASC = "asc";
export const HEATMAP_SORT_DESC = "desc";

const HEATMAP_ROW_LIMIT = 8;
const HEATMAP_MIN_ELEMENTS = 3;
const HEATMAP_MAX_ELEMENTS = 5;

export interface HeatmapTableColumn {
  name: string;
  label: string;
  notSortable?: boolean;
  heading?: boolean;
  format?: Function;
  heatmapSort?: string;
  tooltip?: string;
  checkbox?: string;
  heatmapDisabled?: boolean | Function;
  locked?: boolean;
  disabled?: boolean;
}

export interface HeatmapTableRow {
  // eslint-disable-next-line
  [key: string]: any;
}

export interface HeatmapTableHeatmap {
  [key: string]: number[];
}

@Component({ components: {} })
export default class HeatmapTable extends Vue {
  @Prop() readonly columns!: HeatmapTableColumn[];
  @Prop() readonly rows!: HeatmapTableRow[];
  @Prop({ default: true }) readonly responsive?: boolean;
  @Prop({ default: true }) readonly sortable?: boolean;
  @Prop({ default: false }) readonly heatmap?: boolean;
  @Prop({ default: false }) readonly clickableRows!: boolean;
  @Prop() readonly selectedItems?: string[];
  @Prop({ default: false }) readonly fixedColumns!: boolean;
  @Prop({ default: false }) readonly returnId!: boolean;

  @Watch("rows")
  onRowsChange(value: HeatmapTableRow[]) {
    this.values = [...value];
    this.sortValues();

    this.$nextTick(() => {
      this.checkPosition();
    });
  }

  @Watch("columns")
  onColumnsChange() {
    this.$nextTick(() => {
      this.checkPosition();
    });
  }

  @Watch("selectedItems")
  onSelectedItemsChange(value: string[]) {}

  values: HeatmapTableRow[] = [];
  orderBy: {
    name: string;
    sort: string;
  } = {
    name: "",
    sort: HEATMAP_SORT_DESC,
  };

  position = {
    overflow: false,
    scrolledToRight: false,
    scrolledToLeft: true,
  };
  private checkPositionEnabled: boolean | null = null;

  get heatmapColumns(): HeatmapTableColumn[] {
    if (this.heatmap == false) {
      return [];
    }

    return this.columns.filter((column) => {
      return column.heading != true;
    });
  }

  get heatmapValues(): HeatmapTableHeatmap {
    if (this.heatmap == false) {
      return {};
    }

    const values: HeatmapTableHeatmap = {};

    this.heatmapColumns.forEach((column) => {
      values[column.name] = this.values.map((row) => {
        return row[column.name] as number;
      });

      values[column.name].sort((a: number, b: number) => {
        const heatmapSort = column.heatmapSort || HEATMAP_SORT_DESC;

        if (a < b) {
          return heatmapSort == HEATMAP_SORT_ASC ? -1 : 1;
        } else if (a > b) {
          return heatmapSort == HEATMAP_SORT_DESC ? -1 : 1;
        }

        return 0;
      });
    });

    return values;
  }

  getElementWidth(): number {
    return this.$refs.inner
      ? (this.$refs.inner as HTMLDivElement).clientWidth
      : 0;
  }

  getTableWidth(): number {
    return this.$refs.table
      ? (this.$refs.table as HTMLTableElement).clientWidth
      : 0;
  }

  created() {
    this.values = [...this.rows];
  }

  destroyed() {
    window.removeEventListener("resize", this.checkPosition);
  }

  mounted() {
    window.addEventListener("resize", this.checkPosition);
    this.checkPosition();
  }

  checkPosition() {
    if (this.fixedColumns) {
      return;
    }

    this.position.overflow =
      (this.responsive || true) &&
      this.getElementWidth() < this.getTableWidth();
    this.position.scrolledToRight =
      (this.$refs.inner as HTMLDivElement).scrollLeft ==
      this.getTableWidth() - this.getElementWidth();
    this.position.scrolledToLeft =
      (this.$refs.inner as HTMLDivElement).scrollLeft == 0;
  }

  onClickRow(row: HeatmapTableRow) {
    if (!this.clickableRows) {
      return;
    }

    if (row["id"]) {
      const index = this.selectedItems?.indexOf(row["id"]);
      if (index !== undefined && index > -1) {
        this.selectedItems?.splice(index, 1);
      } else this.selectedItems?.push(row["id"]);
    }
  }

  isChecked(row: HeatmapTableRow) {
    if (row["id"]) {
      return this.selectedItems?.find((o) => o === row["id"]);
    }
    return false;
  }

  sortColumn(column: HeatmapTableColumn) {
    if (this.sortable == false || column.notSortable == true) {
      return;
    }

    let sort = HEATMAP_SORT_DESC;

    if (this.orderBy.name == column.name) {
      sort =
        this.orderBy.sort == HEATMAP_SORT_ASC
          ? HEATMAP_SORT_DESC
          : HEATMAP_SORT_ASC;
    }

    this.orderBy.name = column.name;
    this.orderBy.sort = sort;

    this.sortValues();
  }

  sortValues() {
    if (this.orderBy.name == undefined || this.orderBy.name == "") {
      return;
    }

    this.values = this.values.sort((a: HeatmapTableRow, b: HeatmapTableRow) => {
      let aValue = a[this.orderBy.name] || "";
      let bValue = b[this.orderBy.name] || "";
      if (!isNaN(aValue) && !isNaN(bValue)) {
        aValue = Number.parseFloat(aValue);
        bValue = Number.parseFloat(bValue);
      }

      if (aValue < bValue) {
        return this.orderBy.sort == HEATMAP_SORT_ASC ? -1 : 1;
      } else if (aValue > bValue) {
        return this.orderBy.sort == HEATMAP_SORT_ASC ? 1 : -1;
      }

      return 0;
    });
  }

  scrollToLeft() {
    (this.$refs.inner as HTMLDivElement).scrollLeft = 0;
    this.checkPosition();
  }

  scrollToRight() {
    (this.$refs.inner as HTMLDivElement).scrollLeft =
      this.getTableWidth() - this.getElementWidth();
    this.checkPosition();
  }

  protected isSortable(column: HeatmapTableColumn) {
    if (this.sortable == false) {
      return false;
    }

    return (column.notSortable || column.disabled) != true;
  }

  protected getSlotName(column: HeatmapTableColumn) {
    return `column_${column.name}`;
  }

  protected hasSlot(column: HeatmapTableColumn) {
    const slotName = this.getSlotName(column);
    return this.$scopedSlots[slotName] != null;
  }

  protected getColumnClass(column: HeatmapTableColumn) {
    const sortable = this.isSortable(column);
    const columnClass: Dictionary = {
      "heatmap-table__column": true,
      "heatmap-table__column--sortable": sortable,
      "heatmap-table__column--sorted":
        sortable && column.name == this.orderBy.name,
      "heatmap-table__column--sorted--asc":
        column.name == this.orderBy.name &&
        this.orderBy.sort == HEATMAP_SORT_ASC,
      "heatmap-table__column--sorted--desc":
        column.name == this.orderBy.name &&
        this.orderBy.sort == HEATMAP_SORT_DESC,
      "heatmap-table__column--head": column.heading,
    };
    return columnClass;
  }

  protected getCellValue(
    column: HeatmapTableColumn,
    row: HeatmapTableRow
  ):
    | string
    | number
    | undefined
    | {
        id: number | string | undefined;
        element: number | string | undefined;
      } {
    if (this.returnId && "id" in row) {
      return { id: row["id"], element: row[column.name] };
    }
    return row[column.name];
  }

  protected getCellDisplayValue(
    column: HeatmapTableColumn,
    row: HeatmapTableRow
  ):
    | string
    | number
    | undefined
    | {
        id: number | string | undefined;
        element: number | string | undefined;
      } {
    const value = this.getCellValue(column, row);

    if (value == undefined) {
      return undefined;
    }

    if (typeof column.format == "function") {
      return column.format(value);
    }

    if (typeof value == "number") {
      return NumberFormatter.format(value);
    }

    return value;
  }

  protected getCellClass(column: HeatmapTableColumn, row: HeatmapTableRow) {
    const columnClass = this.getColumnClass(column);
    const heatmapDisabled =
      typeof column.heatmapDisabled == "function"
        ? column.heatmapDisabled()
        : column.heatmapDisabled == true;

    if (this.heatmap == false || column.heading || heatmapDisabled) {
      return columnClass;
    }

    const index = this.getHeatmapIndex(column, row);

    const cellClass: Dictionary = {};

    if (
      index != undefined &&
      ((this.values.length <= HEATMAP_ROW_LIMIT &&
        index < HEATMAP_MIN_ELEMENTS) ||
        (this.values.length > HEATMAP_ROW_LIMIT &&
          index < HEATMAP_MAX_ELEMENTS))
    ) {
      cellClass[`heatmap-${index}`] = true;
    }

    return {
      ...columnClass,
      ...cellClass,
    };
  }

  protected getHeatmapIndex(
    column: HeatmapTableColumn,
    row: HeatmapTableRow
  ): number | undefined {
    if (
      this.heatmapValues[column.name] == undefined ||
      row[column.name] == undefined
    ) {
      return undefined;
    }

    const value = (row[column.name] as number) || 0;

    if (
      value == 0 ||
      (this.heatmapValues[column.name].length &&
        this.heatmapValues[column.name][0] == 0)
    ) {
      return undefined;
    }

    return this.heatmapValues[column.name].indexOf(value);
  }

  protected getColumnTooltipOptions(column: HeatmapTableColumn) {
    if (column.tooltip === undefined || column.tooltip.length === 0) {
      return {};
    }

    const tooltip = {
      content: column.tooltip,
      placement: "left",
      offset: 0,
    };
    return tooltip;
  }

  protected getRowTooltipOptions(
    column: HeatmapTableColumn,
    row: HeatmapTableRow
  ) {
    if (column.tooltip === undefined || row.tooltip === undefined) {
      return {};
    }

    const tooltip = {
      content: row.tooltip,
      placement: "left",
      offset: 0,
    };
    return tooltip;
  }

  protected getColumnCheckboxOptions(column: HeatmapTableColumn) {
    if (column.checkbox === undefined) {
      return {};
    }

    const tooltip = {
      content: column.tooltip,
      placement: "left",
      offset: 0,
    };
    return tooltip;
  }
}
